import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import MobileMenu from './MobileMenu';
import NavItemLink from './NavItemLink';
import MegaMenu, { useTourMegaMenu, useActivitiesMegaMenu, useVehicleRentalsMegaMenu, megaColumnsToMobileChildren } from './MegaMenu';
import WhatsAppFAB from '../WhatsAppFAB';
import { useSettings } from '../../public-cms/hooks';
import { BRAND_NAME, LOGO_TITLE } from '../../brand/brandAssets';
import { DEFAULT_HEADER_NAV } from '../../lib/sitePages';

/** Only these archive nav items get a mega menu; everything else is a plain link. */
function getMegaType(item) {
    const url = (item.url || '').replace(/\/$/, '');
    if (url === '/tour') return 'nepal';
    if (url === '/international-holidays') return 'international';
    if (url === '/activities') return 'activities';
    if (url === '/vehicle-rentals') return 'vehicle-rentals';
    return null;
}

const DEFAULT_NAV = DEFAULT_HEADER_NAV;

const TICKETING_NAV = DEFAULT_NAV.find((item) => item.label === "Ticketing");
const INSURANCE_NAV = DEFAULT_NAV.find((item) => item.label === "Insurance");

const REQUIRED_TICKETING_CHILDREN = [
    { label: "Domestic Flights", url: "/ticketing/domestic" },
    { label: "International Flights", url: "/ticketing/international" },
    { label: "Direct Flight from Kathmandu", url: "/ticketing/from-kathmandu" },
    { label: "Direct Flight from Pokhara", url: "/ticketing/from-pokhara" },
];

function withTicketingChildren(item) {
    if (!item) return item;
    const isTicketing =
        item.label === "Ticketing" ||
        item.children?.some((child) => child.url?.startsWith("/ticketing"));
    if (!isTicketing) return item;
    const existing = Array.isArray(item.children) ? item.children : [];
    const byUrl = new Map(existing.map((c) => [(c.url || "").replace(/\/$/, ""), c]));
    const merged = REQUIRED_TICKETING_CHILDREN.map((req) => {
        const key = req.url.replace(/\/$/, "");
        return byUrl.get(key) || req;
    });
    // Keep any extra custom ticketing children the CMS may have added.
    existing.forEach((c) => {
        const key = (c.url || "").replace(/\/$/, "");
        if (key.startsWith("/ticketing") && !REQUIRED_TICKETING_CHILDREN.some((r) => r.url === key)) {
            merged.push(c);
        }
    });
    return { ...item, url: item.url || "#", children: merged };
}

function withRequiredNavItems(items) {
    if (!Array.isArray(items) || !items.length) return DEFAULT_NAV;
    let next = items.map(withTicketingChildren);

    const hasTicketing = next.some(
        (item) =>
            item.label === "Ticketing" ||
            item.children?.some((child) => child.url?.startsWith("/ticketing"))
    );
    if (!hasTicketing && TICKETING_NAV) {
        const blogIdx = next.findIndex((item) => item.label === "Blog" || item.url === "/blog");
        const insertAt = blogIdx >= 0 ? blogIdx : next.length;
        next = [...next.slice(0, insertAt), TICKETING_NAV, ...next.slice(insertAt)];
    }

    // Point legacy external Himalayan Guardian links at the embedded /insurance page.
    next = next.map((item) => {
        const url = item.url || "";
        const isLegacyInsurance =
            item.label === "Insurance" ||
            url.includes("himalayanguardian.com") ||
            url.includes("hgn.com.np") ||
            url.includes("book.himalayanguardian.com");
        if (isLegacyInsurance && url !== "/insurance") {
            return { label: item.label || "Insurance", url: "/insurance" };
        }
        return item;
    });

    const hasInsurance = next.some(
        (item) => item.label === "Insurance" || (item.url || "").replace(/\/$/, "") === "/insurance"
    );
    if (!hasInsurance && INSURANCE_NAV) {
        const blogIdx = next.findIndex((item) => item.label === "Blog" || item.url === "/blog");
        const insertAt = blogIdx >= 0 ? blogIdx : next.length;
        next = [...next.slice(0, insertAt), INSURANCE_NAV, ...next.slice(insertAt)];
    }

    return next;
}

/**
 * Contact is handled by the Book Now CTA, and Services now lives in the
 * footer's Quick Links column — keep both out of the main nav.
 */
function navForHeader(items) {
    return withRequiredNavItems(items).filter((item) => {
        if (item.children?.length) return true;
        const url = (item.url || "").replace(/\/$/, "");
        const label = (item.label || "").toLowerCase();
        return !(url === "/contact" || label === "contact" || url === "/service" || label === "services");
    });
}

function HeaderOne() {
    const settings = useSettings();
    const nav = navForHeader(
        Array.isArray(settings.headerNav) && settings.headerNav.length ? settings.headerNav : DEFAULT_NAV
    );
    const [isSticky, setIsSticky] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const nepalColumns = useTourMegaMenu('nepal');
    const intlColumns = useTourMegaMenu('international');
    const activityColumns = useActivitiesMegaMenu();
    const vehicleColumns = useVehicleRentalsMegaMenu();
    const megaDataByType = {
        nepal: { columns: nepalColumns, market: 'nepal', viewAllLabel: 'View All Nepal Experiences', viewAllUrl: '/tour' },
        international: { columns: intlColumns, market: 'international', viewAllLabel: 'View All International Holidays', viewAllUrl: '/international-holidays' },
        activities: { columns: activityColumns, market: 'activities', viewAllLabel: 'View All Activities', viewAllUrl: '/activities', isActivity: true },
        'vehicle-rentals': { columns: vehicleColumns, market: undefined, viewAllLabel: 'View All Vehicle Rentals', viewAllUrl: '/vehicle-rentals', isVehicle: true },
    };

    const mobileNav = nav.map((item) => {
        const type = getMegaType(item);
        const data = type && megaDataByType[type];
        if (!data || !data.columns.length) return item;
        return {
            ...item,
            children: megaColumnsToMobileChildren(data.columns, data.market, data.viewAllLabel, data.viewAllUrl, data.isVehicle, data.isActivity),
        };
    });

    const stickyRef = useRef(null);

    useEffect(() => {
        const topBar = document.querySelector("header.th-header .header-top");
        const wrapper = stickyRef.current;
        const menu = wrapper?.querySelector(".menu-area");

        const setSticky = (next) => {
            if (!wrapper || !menu) {
                setIsSticky(next);
                return;
            }
            if (next) {
                // Hold the menu's height in flow before fixing it, so the
                // page doesn't jump upward when sticky engages.
                wrapper.style.height = `${menu.offsetHeight}px`;
            } else {
                wrapper.style.height = "";
            }
            setIsSticky(next);
        };

        const attachScrollSticky = () => {
            const handleScroll = () => setSticky(window.scrollY > 80);
            handleScroll();
            window.addEventListener("scroll", handleScroll, { passive: true });
            return () => window.removeEventListener("scroll", handleScroll);
        };

        // Top bar is hidden below md — IntersectionObserver on display:none
        // would lock sticky on permanently, so use scroll on small screens.
        const mq = window.matchMedia("(min-width: 768px)");
        let cleanup = () => {};

        const bind = () => {
            cleanup();
            if (!mq.matches || !topBar || typeof IntersectionObserver === "undefined") {
                cleanup = attachScrollSticky();
                return;
            }
            const observer = new IntersectionObserver(
                ([entry]) => setSticky(!entry.isIntersecting),
                { threshold: 0 }
            );
            observer.observe(topBar);
            cleanup = () => observer.disconnect();
        };

        bind();
        mq.addEventListener("change", bind);
        return () => {
            mq.removeEventListener("change", bind);
            cleanup();
        };
    }, []);

    const whatsappDigits = String(settings.whatsappNumber || settings.contactPhone || "").replace(/[^\d]/g, "");
    const whatsappHref = whatsappDigits ? `https://wa.me/${whatsappDigits}` : "";
    const socials = [
        { url: settings.facebookUrl, icon: "fab fa-facebook-f", label: "Facebook" },
        { url: settings.instagramUrl, icon: "fab fa-instagram", label: "Instagram" },
        { url: settings.youtubeUrl, icon: "fab fa-youtube", label: "YouTube" },
        { url: settings.tripadvisorUrl, icon: "fab fa-tripadvisor", label: "TripAdvisor" },
    ].filter((s) => s.url);

    return (
        <>
            <header className="th-header header-layout1">
                <div className="header-top d-none d-md-block">
                    <div className="container th-container">
                        <div className="row justify-content-center justify-content-xl-between align-items-center">
                            <div className="col-auto d-none d-md-block">
                                <div className="header-links">
                                    <ul>
                                        <li className="d-none d-xl-inline-block">
                                            <i className="fa-sharp fa-regular fa-location-dot" />
                                            <span>{settings.address || "Kathmandu, Nepal"}</span>
                                        </li>
                                        <li className="d-none d-xl-inline-block">
                                            <i className="fa-regular fa-clock" />
                                            <span>{settings.officeHours || "Sun to Fri: 8:00 am - 7:00 pm"}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="col-auto">
                                <div className="header-right">
                                    <div className="header-links">
                                        <ul>
                                            {whatsappHref && (
                                                <li>
                                                    <a
                                                        href={whatsappHref}
                                                        className="di-header-whatsapp"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        aria-label="Chat on WhatsApp"
                                                    >
                                                        <i className="fab fa-whatsapp" aria-hidden="true" />
                                                        <span>WhatsApp</span>
                                                    </a>
                                                </li>
                                            )}
                                            <li>
                                                <Link to="/faq">FAQ</Link>
                                            </li>
                                            <li>
                                                <Link to="/contact">Support</Link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    ref={stickyRef}
                    className={`sticky-wrapper ${isSticky ? "sticky" : ""}`}
                >
                    <div className="menu-area">
                        <div className="container th-container">
                            <div className="di-header-bar">
                                <div className="di-header-bar__logo">
                                    <Link
                                        to="/"
                                        className="di-header-logo-link"
                                        aria-label={BRAND_NAME}
                                    >
                                        <img
                                            src={LOGO_TITLE}
                                            alt={BRAND_NAME}
                                            className="di-header-logo-img"
                                            width="1850"
                                            height="303"
                                            decoding="async"
                                        />
                                    </Link>
                                </div>

                                <nav className="main-menu di-header-bar__nav d-none d-xl-block">
                                    <ul>
                                        {nav.map((item, i) => {
                                            const megaType = getMegaType(item);
                                            const megaData = megaType && megaDataByType[megaType];
                                            if (megaData && megaData.columns.length) {
                                                return (
                                                    <li key={i} className="menu-item-has-children di-mega-menu-item">
                                                        <NavItemLink to={item.url || "#"} external={item.external}>
                                                            {item.label}
                                                        </NavItemLink>
                                                        <MegaMenu
                                                            columns={megaData.columns}
                                                            market={megaData.market}
                                                            viewAllLabel={megaData.viewAllLabel}
                                                            viewAllUrl={megaData.viewAllUrl}
                                                            isVehicle={megaData.isVehicle}
                                                            isActivity={megaData.isActivity}
                                                        />
                                                    </li>
                                                );
                                            }
                                            return (
                                                <li key={i} className={item.children?.length ? "menu-item-has-children" : ""}>
                                                    <NavItemLink to={item.url || "#"} external={item.external}>
                                                        {item.label}
                                                    </NavItemLink>
                                                    {item.children?.length > 0 && (
                                                        <ul className="sub-menu">
                                                            {item.children.map((child, ci) => (
                                                                <li key={ci}>
                                                                    <NavItemLink to={child.url || "#"} external={child.external}>
                                                                        {child.label}
                                                                    </NavItemLink>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </nav>

                                <div className="di-header-bar__actions">
                                    <div className="header-button d-none d-xl-block">
                                        <Link to="/contact" className="th-btn th-btn-accent di-header-book-btn">
                                            Book Now
                                        </Link>
                                    </div>
                                    <button
                                        type="button"
                                        className="th-menu-toggle d-block d-xl-none"
                                        onClick={() => setIsMobileMenuOpen(true)}
                                        aria-label="Open menu"
                                    >
                                        <i className="far fa-bars" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div
                            className="logo-bg bg-mask"
                            style={{
                                WebkitMaskImage: "url(/assets/img/logo_bg_mask.png)",
                                maskImage: "url()",
                            }}
                        />
                    </div>
                </div>
            </header>
            <MobileMenu
                isOpen={isMobileMenuOpen}
                onClose={() => setIsMobileMenuOpen(false)}
                nav={mobileNav}
                socials={socials}
            />
            <WhatsAppFAB />
        </>
    );
}

export default HeaderOne;
